import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Dialog,
    DialogContent,
    DialogTitle,
} from "../components/ui/dialog";
import { api, humanBytes, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
    Download,
    Trash2,
    RefreshCw,
    Image as ImageIcon,
    Video as VideoIcon,
    FileType2,
    FileText,
    FileQuestion,
    Sparkles,
    Loader2,
    Pencil,
    Check,
    X,
    Plus,
    FolderKanban,
    Star,
    CheckCircle2,
    Upload,
    ChevronLeft,
    ChevronRight,
    Link2,
} from "lucide-react";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import FileComments from "./FileComments";
import FileShareDialog from "./FileShareDialog";
import { toast } from "sonner";

const KIND_ICONS = {
    image: ImageIcon,
    video: VideoIcon,
    pdf: FileType2,
    text: FileText,
    other: FileQuestion,
};

export default function FileDetailDialog({ fileId, onClose, onDeleted, onUpdated, onPrev, onNext }) {
    const navigate = useNavigate();
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [file, setFile] = useState(null);
    const [error, setError] = useState("");
    const [editingName, setEditingName] = useState(false);
    const [nameDraft, setNameDraft] = useState("");
    const [editingDesc, setEditingDesc] = useState(false);
    const [descDraft, setDescDraft] = useState("");
    const [tagDraft, setTagDraft] = useState("");
    const [saving, setSaving] = useState(false);
    const [replaceOpen, setReplaceOpen] = useState(false);
    const [replaceFile, setReplaceFile] = useState(null);
    const [replaceReprocess, setReplaceReprocess] = useState(true);
    const [replacing, setReplacing] = useState(false);
    const [shareOpen, setShareOpen] = useState(false);
    const replaceInputRef = useRef(null);
    const videoRef = useRef(null);

    const load = () => {
        api.get(`/files/${fileId}`)
            .then((r) => setFile(r.data))
            .catch((e) => setError(formatApiError(e)));
    };

    useEffect(() => {
        setFile(null);
        setEditingName(false);
        setEditingDesc(false);
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fileId]);

    // Keyboard navigation between files (lightbox)
    useEffect(() => {
        const handler = (e) => {
            const tag = document.activeElement?.tagName;
            if (tag === "INPUT" || tag === "TEXTAREA") return;
            if (e.key === "ArrowLeft" && onPrev) onPrev();
            if (e.key === "ArrowRight" && onNext) onNext();
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [onPrev, onNext]);

    useEffect(() => {
        if (!file || file.status !== "processing") return;
        const t = setTimeout(load, 4000);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [file]);

    const patch = async (payload) => {
        setSaving(true);
        try {
            const { data } = await api.patch(`/files/${fileId}`, payload);
            setFile((prev) => ({ ...prev, ...data }));
            onUpdated?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    const saveName = async () => {
        const trimmed = nameDraft.trim();
        if (!trimmed) {
            toast.error("Naam mag niet leeg zijn");
            return;
        }
        await patch({ original_name: trimmed });
        setEditingName(false);
    };

    const saveDesc = async () => {
        await patch({ ai_description: descDraft });
        setEditingDesc(false);
    };

    const addTag = async () => {
        const v = tagDraft.trim().toLowerCase();
        if (!v) return;
        const next = Array.from(new Set([...(file.ai_tags || []), v])).slice(0, 30);
        await patch({ ai_tags: next });
        setTagDraft("");
    };

    const removeTag = async (tag) => {
        const next = (file.ai_tags || []).filter((t) => t !== tag);
        await patch({ ai_tags: next });
    };

    const remove = async () => {
        try {
            await api.delete(`/files/${fileId}`);
            toast.success("Bestand verwijderd");
            onDeleted?.();
            onClose();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const reprocess = async () => {
        try {
            await api.post(`/files/${fileId}/reprocess`);
            toast.success("AI-beschrijving wordt opnieuw gegenereerd");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const goToFolder = () => {
        if (!file) return;
        onClose();
        navigate(`/projects/${file.project_id}`);
    };

    const toggleApprove = async () => {
        try {
            const { data } = await api.post(`/files/${fileId}/approve`);
            setFile((p) => ({ ...p, approved: data.approved }));
            toast.success(data.approved ? "Goedgekeurd" : "Goedkeuring ingetrokken");
            onUpdated?.();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const toggleFavorite = async () => {
        try {
            const { data } = await api.post(`/files/${fileId}/favorite`);
            setFile((p) => ({ ...p, favorite: data.favorite }));
            onUpdated?.();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const submitReplace = async () => {
        if (!replaceFile) {
            toast.error("Kies eerst een bestand");
            return;
        }
        setReplacing(true);
        try {
            const fd = new FormData();
            fd.append("file", replaceFile);
            fd.append("reprocess_ai", replaceReprocess ? "true" : "false");
            const { data } = await api.post(`/files/${fileId}/replace`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setFile(data);
            toast.success(
                replaceReprocess
                    ? "Bestand vervangen — AI analyseert opnieuw"
                    : "Bestand vervangen"
            );
            onUpdated?.();
            setReplaceOpen(false);
            setReplaceFile(null);
            setReplaceReprocess(true);
            // reload to ensure fresh preview/download URLs
            setTimeout(load, 300);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setReplacing(false);
        }
    };

    const Icon = file ? KIND_ICONS[file.kind] || FileQuestion : FileQuestion;

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent
                className="max-w-6xl w-[calc(100%-1rem)] max-h-[92vh] overflow-y-auto p-4 md:p-6"
                data-testid="file-detail-dialog"
            >
                <DialogTitle className="sr-only">Bestand detail</DialogTitle>
                {error && <div className="text-red-600 text-sm">{error}</div>}
                {!file ? (
                    <div className="py-20 flex justify-center text-zinc-500">
                        <Loader2 className="w-5 h-5 animate-spin" />
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4 md:gap-6">
                        <div className="md:col-span-3 flex flex-col gap-3">
                            <div className="relative">
                                {file.kind === "video" && file.preview_url ? (
                                    <video
                                        ref={videoRef}
                                        src={file.preview_url}
                                        controls
                                        playsInline
                                        className="w-full max-h-[75vh] bg-black rounded-md border border-zinc-200"
                                        data-testid="file-video"
                                    />
                                ) : (
                                    <div className="bg-zinc-100 border border-zinc-200 rounded-md flex items-center justify-center overflow-hidden h-[60vh] md:h-[75vh]">
                                        {file.kind === "image" && file.preview_url ? (
                                            <img
                                                src={file.preview_url}
                                                alt={file.original_name}
                                                className="w-full h-full object-contain bg-zinc-50"
                                            />
                                        ) : file.kind === "pdf" && file.preview_url ? (
                                            <iframe
                                                src={file.preview_url}
                                                title={file.original_name}
                                                className="w-full h-full bg-white"
                                            />
                                        ) : (
                                            <div className="flex flex-col items-center gap-3 text-zinc-400">
                                                <Icon className="w-16 h-16" />
                                                <span className="text-xs font-mono uppercase">{file.kind}</span>
                                            </div>
                                        )}
                                    </div>
                                )}
                                {onPrev && (
                                    <button
                                        type="button"
                                        onClick={onPrev}
                                        className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/50 hover:bg-[#0044FF] text-white flex items-center justify-center backdrop-blur-sm transition-colors"
                                        aria-label="Vorig bestand"
                                        data-testid="file-nav-prev"
                                    >
                                        <ChevronLeft className="w-5 h-5" />
                                    </button>
                                )}
                                {onNext && (
                                    <button
                                        type="button"
                                        onClick={onNext}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/50 hover:bg-[#0044FF] text-white flex items-center justify-center backdrop-blur-sm transition-colors"
                                        aria-label="Volgend bestand"
                                        data-testid="file-nav-next"
                                    >
                                        <ChevronRight className="w-5 h-5" />
                                    </button>
                                )}
                            </div>

                            {/* Unified action toolbar directly under preview */}
                            <div className="flex flex-wrap items-center gap-2 p-2 rounded-md border border-zinc-200 bg-zinc-50/60">
                                <button
                                    onClick={toggleApprove}
                                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border transition-all ${
                                        file.approved
                                            ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                                            : "bg-white border-zinc-200 text-zinc-700 hover:bg-zinc-50"
                                    }`}
                                    data-testid="file-approve-toggle"
                                >
                                    <CheckCircle2 className={`w-4 h-4 ${file.approved ? "fill-emerald-500 text-white" : ""}`} />
                                    {file.approved ? "Goedgekeurd" : "Goedkeuren"}
                                </button>
                                <button
                                    onClick={toggleFavorite}
                                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border transition-all ${
                                        file.favorite
                                            ? "bg-amber-50 border-amber-200 text-amber-700"
                                            : "bg-white border-zinc-200 text-zinc-700 hover:bg-zinc-50"
                                    }`}
                                    data-testid="file-favorite-toggle"
                                >
                                    <Star className={`w-4 h-4 ${file.favorite ? "fill-amber-500 text-amber-500" : ""}`} />
                                    {file.favorite ? "Favoriet" : "Favoriet"}
                                </button>
                                <a
                                    href={file.download_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-[#0044FF] text-white hover:bg-[#0033CC]"
                                    data-testid="file-download"
                                >
                                    <Download className="w-4 h-4" /> Download
                                </a>
                                <button
                                    onClick={goToFolder}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-white border border-zinc-200 text-zinc-700 hover:bg-zinc-50"
                                    data-testid="file-go-to-folder"
                                >
                                    <FolderKanban className="w-4 h-4" /> Ga naar map
                                </button>
                                <button
                                    onClick={() => setShareOpen(true)}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-white border border-zinc-200 text-zinc-700 hover:bg-zinc-50"
                                    data-testid="file-share-open"
                                >
                                    <Link2 className="w-4 h-4" /> Delen
                                </button>
                                <div className="flex-1" />
                                <button
                                    onClick={reprocess}
                                    title="Opnieuw analyseren"
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium bg-white border border-zinc-200 text-zinc-700 hover:bg-zinc-50"
                                    data-testid="file-reprocess"
                                >
                                    <RefreshCw className="w-4 h-4" />
                                    <span className="hidden lg:inline">Opnieuw</span>
                                </button>
                                {isAdmin && (
                                    <button
                                        onClick={() => {
                                            setReplaceFile(null);
                                            setReplaceReprocess(true);
                                            setReplaceOpen(true);
                                        }}
                                        title="Vervang bestand"
                                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium bg-white border border-zinc-200 text-zinc-700 hover:bg-zinc-50"
                                        data-testid="file-replace"
                                    >
                                        <Upload className="w-4 h-4" />
                                        <span className="hidden lg:inline">Vervang</span>
                                    </button>
                                )}
                                <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                        <button
                                            title="Verwijderen"
                                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium bg-white border border-red-200 text-red-700 hover:bg-red-50"
                                            data-testid="file-delete"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                            <span className="hidden lg:inline">Verwijder</span>
                                        </button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                        <AlertDialogHeader>
                                            <AlertDialogTitle>Bestand verwijderen?</AlertDialogTitle>
                                            <AlertDialogDescription>
                                                "{file.original_name}" wordt permanent verwijderd uit je opslag.
                                            </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                            <AlertDialogCancel>Annuleer</AlertDialogCancel>
                                            <AlertDialogAction
                                                onClick={remove}
                                                className="bg-red-600 hover:bg-red-700"
                                            >
                                                Verwijderen
                                            </AlertDialogAction>
                                        </AlertDialogFooter>
                                    </AlertDialogContent>
                                </AlertDialog>
                            </div>
                        </div>

                        <div className="md:col-span-2 flex flex-col gap-4">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">
                                    Bestand
                                </div>
                                {editingName ? (
                                    <div className="flex gap-2 items-start">
                                        <Input
                                            autoFocus
                                            value={nameDraft}
                                            onChange={(e) => setNameDraft(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") saveName();
                                                if (e.key === "Escape") setEditingName(false);
                                            }}
                                            data-testid="file-name-input"
                                            className="font-display text-xl font-bold tracking-tight"
                                        />
                                        <button
                                            onClick={saveName}
                                            disabled={saving}
                                            className="p-2 bg-[#0044FF] text-white rounded-md hover:bg-[#0033CC]"
                                            data-testid="file-name-save"
                                            aria-label="Opslaan"
                                        >
                                            <Check className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={() => setEditingName(false)}
                                            className="p-2 border border-zinc-200 rounded-md hover:bg-zinc-50"
                                            aria-label="Annuleer"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                ) : (
                                    <div className="flex items-start gap-2 group">
                                        <h2 className="font-display text-2xl tracking-tight font-bold leading-tight break-all flex-1">
                                            {file.original_name}
                                        </h2>
                                        <button
                                            onClick={() => {
                                                setNameDraft(file.original_name);
                                                setEditingName(true);
                                            }}
                                            className="p-1.5 text-zinc-400 hover:text-zinc-900 rounded-md hover:bg-zinc-100"
                                            data-testid="file-name-edit"
                                            aria-label="Naam wijzigen"
                                        >
                                            <Pencil className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                )}
                                <div className="text-xs font-mono text-zinc-500 mt-2">
                                    {file.mime_type} · {humanBytes(file.size_bytes)} ·{" "}
                                    {new Date(file.created_at).toLocaleString("nl-BE")}
                                </div>
                                {file.version > 1 && (
                                    <div
                                        className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-sm bg-amber-50 border border-amber-200 text-amber-800 text-[11px] font-mono"
                                        data-testid="file-version-badge"
                                    >
                                        <RefreshCw className="w-3 h-3" />
                                        v{file.version}
                                        {file.replaced_at && (
                                            <span className="text-amber-700/80">
                                                · vervangen{" "}
                                                {new Date(file.replaced_at).toLocaleString("nl-BE")}
                                            </span>
                                        )}
                                    </div>
                                )}

                            </div>

                            <div className="border-t border-zinc-200 pt-4">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-500">
                                        <Sparkles className="w-3 h-3" /> AI Beschrijving
                                    </div>
                                    {!editingDesc && file.status !== "processing" && (
                                        <button
                                            onClick={() => {
                                                setDescDraft(file.ai_description || "");
                                                setEditingDesc(true);
                                            }}
                                            className="p-1.5 text-zinc-400 hover:text-zinc-900 rounded-md hover:bg-zinc-100"
                                            data-testid="file-desc-edit"
                                            aria-label="Beschrijving wijzigen"
                                        >
                                            <Pencil className="w-3.5 h-3.5" />
                                        </button>
                                    )}
                                </div>
                                {file.status === "processing" ? (
                                    <div className="flex items-center gap-2 text-zinc-500 text-sm">
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        AI analyseert dit bestand…
                                    </div>
                                ) : editingDesc ? (
                                    <div className="space-y-2">
                                        <Textarea
                                            rows={5}
                                            value={descDraft}
                                            onChange={(e) => setDescDraft(e.target.value)}
                                            data-testid="file-desc-input"
                                            className="text-sm"
                                        />
                                        <div className="flex gap-2">
                                            <button
                                                onClick={saveDesc}
                                                disabled={saving}
                                                className="px-3 py-1.5 bg-[#0044FF] text-white rounded-md text-sm hover:bg-[#0033CC]"
                                                data-testid="file-desc-save"
                                            >
                                                Opslaan
                                            </button>
                                            <button
                                                onClick={() => setEditingDesc(false)}
                                                className="px-3 py-1.5 border border-zinc-200 rounded-md text-sm hover:bg-zinc-50"
                                            >
                                                Annuleer
                                            </button>
                                        </div>
                                    </div>
                                ) : file.ai_description ? (
                                    <p className="text-sm text-zinc-800 leading-relaxed whitespace-pre-wrap">
                                        {file.ai_description}
                                    </p>
                                ) : (
                                    <div className="text-sm text-zinc-500">
                                        Geen beschrijving. Klik op het potloodje om er een toe te voegen.
                                    </div>
                                )}

                                {/* Tags */}
                                <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mt-4 mb-2">
                                    Tags
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                    {(file.ai_tags || []).map((t) => (
                                        <span
                                            key={t}
                                            className="inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-zinc-100 border border-zinc-200 text-xs font-mono text-zinc-700 group"
                                            data-testid={`tag-${t}`}
                                        >
                                            {t}
                                            <button
                                                onClick={() => removeTag(t)}
                                                className="text-zinc-400 hover:text-red-600 ml-0.5"
                                                aria-label={`Verwijder ${t}`}
                                            >
                                                <X className="w-3 h-3" />
                                            </button>
                                        </span>
                                    ))}
                                    <div className="inline-flex items-center gap-1 border border-zinc-200 rounded-sm bg-white pl-2">
                                        <input
                                            type="text"
                                            value={tagDraft}
                                            onChange={(e) => setTagDraft(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    e.preventDefault();
                                                    addTag();
                                                }
                                            }}
                                            placeholder="nieuwe tag…"
                                            className="text-xs font-mono outline-none w-24 py-1"
                                            data-testid="tag-input"
                                        />
                                        <button
                                            onClick={addTag}
                                            disabled={!tagDraft.trim()}
                                            className="p-1 text-[#0044FF] hover:bg-zinc-100 rounded-sm disabled:text-zinc-300"
                                            aria-label="Tag toevoegen"
                                            data-testid="tag-add"
                                        >
                                            <Plus className="w-3 h-3" />
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Comments */}
                            <FileComments fileId={fileId} mode="portal" videoRef={videoRef} />
                        </div>
                    </div>
                )}
            </DialogContent>

            <FileShareDialog
                fileId={fileId}
                open={shareOpen}
                onClose={() => setShareOpen(false)}
            />

            {/* Replace file dialog (admin only) */}
            <AlertDialog open={replaceOpen} onOpenChange={setReplaceOpen}>
                <AlertDialogContent data-testid="file-replace-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Bestand vervangen</AlertDialogTitle>
                        <AlertDialogDescription>
                            De inhoud wordt vervangen door een nieuwe versie. Opmerkingen,
                            favoriet-status en de link blijven behouden.{" "}
                            <strong>De goedkeuring wordt gereset</strong> zodat de klant de
                            nieuwe versie opnieuw kan goedkeuren.
                        </AlertDialogDescription>
                    </AlertDialogHeader>

                    <div className="space-y-3 py-2">
                        <button
                            type="button"
                            onClick={() => replaceInputRef.current?.click()}
                            className="w-full border-2 border-dashed border-zinc-300 hover:border-[#0044FF] hover:bg-zinc-50 rounded-md p-4 text-sm text-zinc-600 flex items-center justify-center gap-2"
                            data-testid="file-replace-pick"
                        >
                            <Upload className="w-4 h-4" />
                            {replaceFile ? (
                                <span className="truncate">
                                    {replaceFile.name} · {humanBytes(replaceFile.size)}
                                </span>
                            ) : (
                                <span>Kies een nieuw bestand…</span>
                            )}
                        </button>
                        <input
                            ref={replaceInputRef}
                            type="file"
                            className="hidden"
                            onChange={(e) => setReplaceFile(e.target.files?.[0] || null)}
                            data-testid="file-replace-input"
                        />

                        <label className="flex items-start gap-2 text-sm cursor-pointer select-none">
                            <input
                                type="checkbox"
                                checked={replaceReprocess}
                                onChange={(e) => setReplaceReprocess(e.target.checked)}
                                className="mt-0.5 w-4 h-4 accent-[#0044FF]"
                                data-testid="file-replace-reprocess"
                            />
                            <span>
                                AI-beschrijving en tags opnieuw genereren
                                <span className="block text-xs text-zinc-500">
                                    Aanbevolen wanneer de visuele inhoud wijzigt.
                                </span>
                            </span>
                        </label>
                    </div>

                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={replacing}>Annuleer</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => {
                                e.preventDefault();
                                submitReplace();
                            }}
                            disabled={!replaceFile || replacing}
                            className="bg-[#0044FF] hover:bg-[#0033CC]"
                            data-testid="file-replace-submit"
                        >
                            {replacing ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin mr-2" /> Vervangen…
                                </>
                            ) : (
                                "Vervangen"
                            )}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Dialog>
    );
}
